import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Role } from '@gsm/partage';
import { api, ErreurApi, type Profil } from '../api/client';

interface ContexteAuth {
  profil: Profil | null;
  chargement: boolean;
  connexion: (login: string, motDePasse: string) => Promise<void>;
  deconnexion: () => Promise<void>;
  rafraichir: () => Promise<void>;
  aRole: (...roles: Role[]) => boolean;
}

const Contexte = createContext<ContexteAuth | null>(null);

export function FournisseurAuth({ children }: { children: ReactNode }) {
  const [profil, setProfil] = useState<Profil | null>(null);
  const [chargement, setChargement] = useState(true);

  const rafraichir = useCallback(async () => {
    try {
      setProfil(await api.get<Profil>('/auth/moi'));
    } catch (erreur) {
      // 401 à l'ouverture n'est pas une anomalie : c'est un visiteur non
      // connecté. Toute autre erreur mérite d'être remontée.
      if (!(erreur instanceof ErreurApi) || erreur.statut !== 401) {
        console.error(erreur);
      }
      setProfil(null);
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => {
    void rafraichir();
  }, [rafraichir]);

  const connexion = useCallback(async (login: string, motDePasse: string) => {
    setProfil(await api.post<Profil>('/auth/login', { login, motDePasse }));
  }, []);

  const deconnexion = useCallback(async () => {
    await api.post<void>('/auth/logout');
    setProfil(null);
  }, []);

  const valeur = useMemo<ContexteAuth>(
    () => ({
      profil,
      chargement,
      connexion,
      deconnexion,
      rafraichir,
      // L'ADMIN est superadministrateur : il satisfait tout contrôle de rôle,
      // donc voit toutes les pages et toutes les actions (décision du 04/08/2026).
      aRole: (...roles: Role[]) =>
        profil
          ? profil.roles.includes('ADMIN') || roles.some((r) => profil.roles.includes(r))
          : false,
    }),
    [profil, chargement, connexion, deconnexion, rafraichir],
  );

  return <Contexte.Provider value={valeur}>{children}</Contexte.Provider>;
}

export function useAuth(): ContexteAuth {
  const contexte = useContext(Contexte);
  if (!contexte) throw new Error('useAuth doit être utilisé dans FournisseurAuth');
  return contexte;
}
